import { ChangeDetectionStrategy, Component, computed, input, signal, WritableSignal } from '@angular/core';
import { form, required, validate } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
  AgentOfficeAddressDto,
  AgentOpeningHoursDto,
  AgentProfileDto,
  AgentSocialLinksDto,
  AgentSpecialty,
  AgentType,
  Country,
  FrenchRegion,
  InterventionZoneDto,
  PreferredCityDto,
} from '@workspace/data-types';
import { AbstractSignalFormComponent } from '@workspace/ng-aura';
import { AgentBioFormComponent } from '../bio';
import { AgentCitiesFormComponent } from '../cities';
import { AgentNetworkFormComponent } from '../network';
import { AgentOfficeHoursFormComponent } from '../office-hours';
import { AgentSocialLinksFormComponent } from '../social-links';
import { AgentSpecialtiesFormComponent } from '../specialties';
import { AgentZonesFormComponent } from '../zones';

export type AgentZoneActivityShowMode =
  'all' | 'zones' | 'cities' | 'network' | 'specialties' | 'bio' | 'socialLinks' | 'officeHours';

export interface AgentZoneActivityFormModel {
  type: AgentType;
  primaryRegion: FrenchRegion | null;
  interventionZones: InterventionZoneDto[];
  preferredCities: PreferredCityDto[];
  network: string;
  specialties: (AgentSpecialty | string)[];
  bio: string;
  socialLinks: AgentSocialLinksDto;
  officeAddress: AgentOfficeAddressDto;
  openingHours: AgentOpeningHoursDto;
}

@Component({
  selector: 'etxe-agent-zone-activity-form',
  imports: [
    MatIconModule,
    MatButtonModule,
    AgentZonesFormComponent,
    AgentCitiesFormComponent,
    AgentNetworkFormComponent,
    AgentSpecialtiesFormComponent,
    AgentBioFormComponent,
    AgentSocialLinksFormComponent,
    AgentOfficeHoursFormComponent,
  ],
  templateUrl: './agent-zone-activity-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentZoneActivityFormComponent extends AbstractSignalFormComponent<
  AgentProfileDto,
  AgentZoneActivityFormModel
> {
  readonly showMode = input<AgentZoneActivityShowMode>('all');

  override model: WritableSignal<AgentZoneActivityFormModel> = signal<AgentZoneActivityFormModel>({
    type: 'titulaire',
    primaryRegion: FrenchRegion.OCCITANIE,
    interventionZones: [{ country: Country.FRANCE, departments: [] }],
    preferredCities: [],
    network: '',
    specialties: [],
    bio: '',
    socialLinks: {},
    officeAddress: {},
    openingHours: this.getDefaultOpeningHours(),
  });

  override formTree = form(this.model, (b) => {
    required(b.primaryRegion);
    validate(b.interventionZones, ({ value }) => {
      const zones = value();
      const fr = zones?.find((z) => z.country === Country.FRANCE);
      if (!fr || fr.departments.length === 0) {
        return {
          kind: 'minDepartments',
          message: 'Au moins un département doit être sélectionné.',
        };
      }
      return null;
    });
  });

  readonly agentType = computed(() => this.model().type);
  readonly useCustomAddress = signal<boolean>(false);

  readonly officialCardAddress = computed(() => {
    const card = this.dto()?.cards?.activeCard;
    if (!card?.address) return null;
    const a = card.address;
    const parts = [
      [a.streetNumber, a.streetType, a.streetName].filter(Boolean).join(' '),
      a.complement,
      [a.zipCode, a.city].filter(Boolean).join(' '),
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  });

  readonly selectedDepartments = computed(() => {
    const zones = this.model().interventionZones;
    const frZone = zones.find((z) => z.country === Country.FRANCE);
    return frZone?.departments || [];
  });

  readonly selectedDepartmentsSummary = computed(() => {
    const depts = this.selectedDepartments();
    if (depts.length === 0) return 'aucun département';
    return depts.slice(0, 5).join(', ') + (depts.length > 5 ? ` +${depts.length - 5}` : '');
  });

  protected override buildModelFromDto(dto: AgentProfileDto): AgentZoneActivityFormModel {
    const defaultZone: InterventionZoneDto = { country: Country.FRANCE, departments: [] };
    const zones = dto?.intervention?.zones?.length ? dto.intervention.zones : [defaultZone];

    const office = dto?.work?.officeAddress;
    const hasCustomAddress = Boolean(office?.streetName || office?.city || office?.zipCode);
    this.useCustomAddress.set(hasCustomAddress);

    return {
      type: dto?.type ?? 'titulaire',
      primaryRegion: dto?.intervention?.primaryRegion ?? FrenchRegion.OCCITANIE,
      interventionZones: zones,
      preferredCities: dto?.intervention?.preferredCities ? [...dto.intervention.preferredCities] : [],
      network: dto?.activity?.network || '',
      specialties: dto?.activity?.specialties ? [...dto.activity.specialties] : [],
      bio: dto?.activity?.bio || '',
      socialLinks: dto?.activity?.socialLinks ? { ...dto.activity.socialLinks } : {},
      officeAddress: office ? { ...office } : {},
      openingHours: dto?.work?.openingHours ? { ...dto.work.openingHours } : this.getDefaultOpeningHours(),
    };
  }

  protected override buildDtoFromModel(model: AgentZoneActivityFormModel): AgentProfileDto {
    const dto = this.dto() || ({} as AgentProfileDto);
    return {
      ...dto,
      type: model.type,
      intervention: {
        ...dto.intervention,
        primaryRegion: model.primaryRegion ?? undefined,
        zones: model.interventionZones,
        preferredCities: model.preferredCities,
      },
      activity: {
        ...dto.activity,
        network: model.network || undefined,
        specialties: model.specialties,
        bio: model.bio ? model.bio.trim() : undefined,
        socialLinks: model.socialLinks,
      },
      work: {
        ...dto.work,
        officeAddress: this.useCustomAddress() ? model.officeAddress : undefined,
        openingHours: model.openingHours,
      },
    };
  }

  setPrimaryRegion(reg: FrenchRegion): void {
    this.model.update((m) => ({ ...m, primaryRegion: reg }));
  }

  onInterventionZonesChange(zones: InterventionZoneDto[]): void {
    const frZone = zones.find((z) => z.country === Country.FRANCE);
    const allowed = new Set((frZone?.departments || []) as string[]);

    // Prune preferred cities that are no longer in selected departments
    const validCities = this.model().preferredCities.filter((city) => {
      const dept = this.getDepartmentCodeFromZip(city.zipCode);
      return allowed.has(dept);
    });

    this.model.update((m) => ({
      ...m,
      interventionZones: zones,
      preferredCities: validCities,
    }));
  }

  onPreferredCitiesChange(cities: PreferredCityDto[]): void {
    this.model.update((m) => ({ ...m, preferredCities: cities }));
  }

  onNetworkChange(network: string): void {
    this.model.update((m) => ({ ...m, network }));
  }

  onSpecialtiesChange(specialties: (AgentSpecialty | string)[]): void {
    this.model.update((m) => ({ ...m, specialties }));
  }

  onBioChange(bio: string): void {
    this.model.update((m) => ({ ...m, bio }));
  }

  onSocialLinksChange(socialLinks: AgentSocialLinksDto): void {
    this.model.update((m) => ({ ...m, socialLinks }));
  }

  onOfficeAddressChange(officeAddress: AgentOfficeAddressDto): void {
    this.model.update((m) => ({ ...m, officeAddress }));
  }

  onUseCustomAddressChange(custom: boolean): void {
    this.useCustomAddress.set(custom);
    if (!custom) {
      this.model.update((m) => ({ ...m, officeAddress: {} }));
    }
  }

  onOpeningHoursChange(openingHours: AgentOpeningHoursDto): void {
    this.model.update((m) => ({ ...m, openingHours }));
  }

  private getDepartmentCodeFromZip(zipCode: string): string {
    if (!zipCode) return '';
    if (zipCode.startsWith('97')) {
      return zipCode.slice(0, 3);
    }
    if (zipCode.startsWith('20')) {
      const num = Number.parseInt(zipCode, 10);
      return num < 20200 ? '2A' : '2B';
    }
    return zipCode.slice(0, 2);
  }

  private getDefaultOpeningHours(): AgentOpeningHoursDto {
    const weekday = {
      isOpen: true,
      byAppointmentOnly: false,
      morningOpen: '09:00',
      morningClose: '12:00',
      afternoonOpen: '14:00',
      afternoonClose: '19:00',
    };
    return {
      monday: { ...weekday },
      tuesday: { ...weekday },
      wednesday: { ...weekday },
      thursday: { ...weekday },
      friday: { ...weekday },
      saturday: { isOpen: true, byAppointmentOnly: true },
      sunday: { isOpen: false },
      notes: '',
    };
  }
}
